// Sound + haptic feedback. Pure Web Audio synthesis — no asset downloads.
// Layered tones (sine + triangle for warmth), full ADSR envelopes, a shared
// reverb send, filtered-noise textures, additive bell synthesis. Gated on
// user preference and `prefers-reduced-motion`.
//
// AudioContext can only be created or resumed in response to a user gesture,
// so we lazy-init on the first `play()` call.

const STORAGE_KEY_MUTED = "latlongago.audio.muted";
const STORAGE_KEY_VOLUME = "latlongago.audio.volume";

type SoundName =
  | "pin"
  | "yearTick"
  | "submit"
  | "stinger.bronze"
  | "stinger.silver"
  | "stinger.gold"
  | "stinger.perfect"
  | "achievement"
  | "timerTick"
  | "timerWarn"
  | "modalOpen"
  | "modalClose"
  | "wager"
  | "hint"
  | "roundAdvance"
  | "gameEnd"
  | "tab"
  | "zoom"
  | "scoreTick"
  | "hotspot"
  | "bonus"
  | "distanceSweep"
  | "error"
  | "pinReset"
  | "onboardingStep"
  | "streak"
  | "yearLock"
  | "mapZoom"
  | "wagerHover"
  | "confettiSparkle"
  | "comeback"
  | "modalThump"
  | "answerPin"
  | "wahWah"
  | "explosion";

interface ToneSpec {
  freq: number;
  type?: OscillatorType;
  duration: number;
  attack?: number;
  decay?: number;
  sustainLevel?: number;
  release?: number;
  level?: number;
  freqEnd?: number;
  pan?: number;
  reverbSend?: number; // 0–1
  startAt?: number;
  detune?: number;
}

class FeedbackService {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private reverbInput: DelayNode | null = null;
  private muted: boolean;
  private volume: number;
  private lastPlayed = new Map<SoundName, number>();
  private readonly listeners = new Set<() => void>();

  constructor() {
    const stored = localStorage.getItem(STORAGE_KEY_MUTED);
    // Sound on by default everywhere — players can mute via the brand-bar
    // toggle if they want quiet. Stored preference wins once set.
    this.muted = stored === "1";

    const storedVol = localStorage.getItem(STORAGE_KEY_VOLUME);
    this.volume = storedVol === null ? 0.6 : clamp01(parseFloat(storedVol));
  }

  isMuted(): boolean {
    return this.muted;
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    localStorage.setItem(STORAGE_KEY_MUTED, muted ? "1" : "0");
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(this.effectiveGain(), this.ctx.currentTime, 0.01);
    }
    this.emit();
  }

  setVolume(volume: number): void {
    this.volume = clamp01(volume);
    localStorage.setItem(STORAGE_KEY_VOLUME, this.volume.toString());
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(this.effectiveGain(), this.ctx.currentTime, 0.01);
    }
    this.emit();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Public sound triggers ----------------------------------------------------

  pinDrop(): void {
    this.play("pin", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Layer 1: warm low thud — sine sweeping down for body.
      this.tone({
        freq: 220,
        freqEnd: 88,
        type: "sine",
        duration: 0.32,
        attack: 0.004,
        decay: 0.06,
        sustainLevel: 0.7,
        release: 0.22,
        level: 0.42,
        reverbSend: 0.18,
        startAt: now
      });
      // Layer 2: filtered noise body — soft impact.
      this.noiseBurst({
        filterType: "lowpass",
        filterFreq: 950,
        filterQ: 0.9,
        duration: 0.09,
        level: 0.14,
        startAt: now
      });
      // Layer 3: high triangle blip — the "click" of contact.
      this.tone({
        freq: 1480,
        type: "triangle",
        duration: 0.07,
        attack: 0.001,
        decay: 0.02,
        sustainLevel: 0.0,
        release: 0.05,
        level: 0.12,
        startAt: now + 0.005
      });
    });
    vibrate(10);
  }

  // Smooth, soft swipe-tick for the year slider. Pitch follows direction —
  // you can hear time itself moving forward / backward.
  yearTick(direction: "up" | "down" = "up"): void {
    if (!this.canPlay("yearTick", 30)) return;
    this.play("yearTick", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      const baseFreq = direction === "up" ? 760 : 620;
      // Single soft sine at lowish freq with very gentle envelope. No hard
      // attack — feels like air, not a click.
      this.tone({
        freq: baseFreq,
        type: "sine",
        duration: 0.08,
        attack: 0.012,
        decay: 0.02,
        sustainLevel: 0.6,
        release: 0.06,
        level: 0.07,
        reverbSend: 0.0,
        startAt: now
      });
      // Whisper of filtered noise so the tick has texture, not just tone.
      this.noiseBurst({
        filterType: "highpass",
        filterFreq: 2200,
        filterQ: 0.6,
        duration: 0.04,
        level: 0.025,
        startAt: now
      });
    });
  }

  submit(): void {
    this.play("submit", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Three-note ascending arpeggio (C5, E5, G5) — confident chord.
      const notes = [523.25, 659.25, 783.99];
      notes.forEach((freq, i) => {
        const start = now + i * 0.04;
        this.tone({
          freq,
          type: "triangle",
          duration: 0.36,
          attack: 0.005,
          decay: 0.08,
          sustainLevel: 0.4,
          release: 0.28,
          level: 0.18,
          reverbSend: 0.32,
          startAt: start
        });
        // Sine octave-up adds shimmer.
        this.tone({
          freq: freq * 2,
          type: "sine",
          duration: 0.28,
          attack: 0.005,
          decay: 0.06,
          sustainLevel: 0.3,
          release: 0.22,
          level: 0.06,
          reverbSend: 0.4,
          startAt: start
        });
      });
    });
    vibrate(20);
  }

  reveal(roundScore: number): void {
    if (roundScore >= 4900) {
      this.stingerPerfect();
    } else if (roundScore >= 4500) {
      this.stingerGold();
    } else if (roundScore >= 4250) {
      this.stingerSilver();
    } else if (roundScore >= 3500) {
      this.stingerBronze();
    }
  }

  stingerPerfect(): void {
    this.play("stinger.perfect", () => this.fanfareChord([523.25, 659.25, 783.99, 987.77, 1318.51], 0.55, true));
    vibrate([20, 30, 40]);
  }

  stingerGold(): void {
    this.play("stinger.gold", () => this.fanfareChord([523.25, 659.25, 783.99, 987.77], 0.5, true));
    vibrate([20, 30, 40]);
  }

  stingerSilver(): void {
    this.play("stinger.silver", () => this.fanfareChord([392.0, 493.88, 587.33, 783.99], 0.42, false));
    vibrate(40);
  }

  stingerBronze(): void {
    this.play("stinger.bronze", () => this.fanfareChord([329.63, 415.3, 493.88], 0.35, false));
    vibrate(30);
  }

  // Two flavors of achievement unlock:
  //   "routine" — Mario-coin: short ascending bell arpeggio, snappy.
  //   "rare"    — full bell ladder with sparkle; for the showpiece unlocks.
  achievement(rarity: "routine" | "rare" = "routine"): void {
    this.play("achievement", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      if (rarity === "rare") {
        // Big sparkle burst into a 6-note ascending bell ladder.
        this.noiseBurst({
          filterType: "bandpass",
          filterFreq: 3000,
          filterFreqEnd: 6800,
          filterQ: 1.6,
          duration: 0.4,
          level: 0.09,
          startAt: now
        });
        const notes = [523.25, 659.25, 880, 1046.5, 1318.51, 1760];
        notes.forEach((freq, i) => {
          this.bell(freq, 0.5 - i * 0.04, 0.16 + (i === notes.length - 1 ? 0.04 : 0), now + i * 0.075);
        });
        vibrate([15, 25, 15, 25, 30, 25]);
      } else {
        // Mario-coin: classic 2-note bell pop.
        this.bell(987.77, 0.18, 0.18, now);
        this.bell(1318.51, 0.32, 0.18, now + 0.085);
        vibrate([15, 25]);
      }
    });
  }

  // Continuous countdown beep that escalates in volume, pitch, and tempo as
  // the timer drains. `intensity` 0 (calm, ~15s) → 1 (alarm, ~2s) → 2 (insane,
  // 0s). Past 1.0 the throttle collapses to ~25ms — basically a continuous trill.
  timerCountdown(intensity: number): void {
    const t = Math.max(0, intensity);
    // Smooth hyperbolic curve: 800ms at t=0, 180ms at t=1, ~25ms at t=2.
    const throttle = Math.max(25, 800 / (1 + t * 4.4));
    if (!this.canPlay("timerTick", throttle)) return;
    this.play("timerTick", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Pitch climbs from 880 → ~2200 Hz; clamp so it doesn't get unpleasant.
      const pitchFactor = Math.min(1, t * 0.7);
      const baseFreq = 880 + pitchFactor * 1320;
      // Volume climbs from 0.06 → 0.42.
      const peak = Math.min(0.42, 0.06 + t * 0.22);
      this.tone({
        freq: baseFreq,
        freqEnd: baseFreq * 0.85,
        type: t > 0.55 ? "triangle" : "sine",
        duration: Math.max(0.04, 0.09 + Math.min(1, t) * 0.06 - Math.max(0, t - 1) * 0.05),
        attack: 0.002,
        decay: 0.02,
        sustainLevel: 0.45,
        release: 0.06,
        level: peak,
        reverbSend: 0.2,
        startAt: now
      });
      // Low-octave reinforcement past halfway — chest-thump for the panic phase.
      if (t > 0.5) {
        this.tone({
          freq: baseFreq * 0.5,
          type: "sine",
          duration: 0.08,
          attack: 0.002,
          decay: 0.015,
          sustainLevel: 0.4,
          release: 0.06,
          level: peak * 0.5,
          startAt: now
        });
      }
    });
    // Haptic kicks in only in the panic half.
    if (t > 0.5) {
      vibrate(8 + Math.round(Math.min(1, t) * 18));
    }
  }

  // Final detonation when the timer hits zero — sub-bass thump, chest-punch
  // body, wide-band noise sweep, and crackling debris tail.
  explosion(): void {
    this.play("explosion", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Sub-bass impact: punches the chest first.
      this.tone({
        freq: 90,
        freqEnd: 32,
        type: "sine",
        duration: 0.55,
        attack: 0.001,
        decay: 0.05,
        sustainLevel: 0.55,
        release: 0.45,
        level: 0.6,
        startAt: now
      });
      // Mid-low body: dirty saw pitch-down, gives the boom its grit.
      this.tone({
        freq: 240,
        freqEnd: 55,
        type: "sawtooth",
        duration: 0.4,
        attack: 0.001,
        decay: 0.04,
        sustainLevel: 0.4,
        release: 0.3,
        level: 0.32,
        reverbSend: 0.35,
        startAt: now
      });
      // Wide-band noise: rumble + initial crack, sweeping down.
      this.noiseBurst({
        filterType: "highpass",
        filterFreq: 280,
        filterFreqEnd: 60,
        filterQ: 0.5,
        duration: 0.75,
        level: 0.5,
        startAt: now
      });
      // Crackling debris — quick bandpass bursts at random delays.
      for (let i = 0; i < 6; i++) {
        const delay = 0.04 + i * 0.07 + Math.random() * 0.03;
        this.noiseBurst({
          filterType: "bandpass",
          filterFreq: 800 + Math.random() * 1500,
          filterQ: 2.5,
          duration: 0.07,
          level: 0.18,
          startAt: now + delay,
        });
      }
      // Long sub rumble tail with reverb — the "lingering" feel.
      this.tone({
        freq: 50,
        freqEnd: 32,
        type: "sine",
        duration: 1.2,
        attack: 0.06,
        decay: 0.3,
        sustainLevel: 0.3,
        release: 0.9,
        level: 0.3,
        reverbSend: 0.55,
        startAt: now + 0.06,
      });
    });
    // Strong haptic kick on mobile.
    vibrate([0, 90, 30, 60]);
  }

  // Legacy wrappers — kept callable in case anything still references them.
  timerTick(): void {
    this.timerCountdown(0.15);
  }

  timerWarn(): void {
    this.timerCountdown(0.85);
    vibrate(15);
  }

  modalOpen(): void {
    this.play("modalOpen", () => this.swoosh("up"));
  }

  modalClose(): void {
    this.play("modalClose", () => this.swoosh("down"));
  }

  // NEW SOUNDS ---------------------------------------------------------------

  // Wager change: 3 distinct pitches per multiplier with a tiny bell timbre.
  wagerChange(wager: 1 | 2 | 3): void {
    this.play("wager", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      const freq = wager === 1 ? 392.0 : wager === 2 ? 523.25 : 659.25;
      this.bell(freq, 0.32, 0.12, now);
      // Slight mirror octave above for shimmer at higher wagers.
      if (wager >= 2) {
        this.tone({
          freq: freq * 2,
          type: "sine",
          duration: 0.26,
          attack: 0.005,
          decay: 0.04,
          sustainLevel: 0.3,
          release: 0.2,
          level: 0.04,
          reverbSend: 0.3,
          startAt: now
        });
      }
    });
    vibrate(8);
  }

  // Hint sounds — distinct per type.
  hintTaken(hint: "continent" | "century" | "compass" | "wildcard" = "continent"): void {
    this.play("hint", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      switch (hint) {
        case "century":
          // Clock-chime: bell with a low "tick" underneath.
          this.bell(659.25, 0.55, 0.18, now);
          this.tone({
            freq: 220,
            type: "sine",
            duration: 0.18,
            attack: 0.005,
            decay: 0.04,
            sustainLevel: 0.4,
            release: 0.12,
            level: 0.1,
            reverbSend: 0.4,
            startAt: now
          });
          break;
        case "compass": {
          // Pitch sweep — feels like a bearing being acquired.
          this.tone({
            freq: 440,
            freqEnd: 880,
            type: "sine",
            duration: 0.32,
            attack: 0.01,
            decay: 0.05,
            sustainLevel: 0.5,
            release: 0.2,
            level: 0.16,
            reverbSend: 0.35,
            startAt: now
          });
          this.noiseBurst({
            filterType: "bandpass",
            filterFreq: 2400,
            filterFreqEnd: 5200,
            filterQ: 1.6,
            duration: 0.28,
            level: 0.05,
            startAt: now
          });
          break;
        }
        case "wildcard":
          // Sparkly random arpeggio.
          [523.25, 880, 1318.51].forEach((freq, i) => {
            this.bell(freq, 0.32 - i * 0.04, 0.12, now + i * 0.05);
          });
          break;
        case "continent":
        default:
          // Earthy: low sustained pad with octave above.
          this.tone({
            freq: 196,
            type: "sine",
            duration: 0.45,
            attack: 0.02,
            decay: 0.1,
            sustainLevel: 0.55,
            release: 0.25,
            level: 0.18,
            reverbSend: 0.5,
            startAt: now
          });
          this.tone({
            freq: 392.0,
            type: "triangle",
            duration: 0.35,
            attack: 0.02,
            decay: 0.06,
            sustainLevel: 0.4,
            release: 0.18,
            level: 0.07,
            reverbSend: 0.5,
            startAt: now
          });
          break;
      }
    });
    vibrate(12);
  }

  // Page-turn whoosh + soft chord on advancing to the next round.
  roundAdvance(): void {
    this.play("roundAdvance", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.noiseBurst({
        filterType: "bandpass",
        filterFreq: 800,
        filterFreqEnd: 2200,
        filterQ: 0.8,
        duration: 0.22,
        level: 0.13,
        startAt: now
      });
      // Quick C-major-ish chord underneath.
      [392.0, 493.88].forEach((freq, i) => {
        this.tone({
          freq,
          type: "sine",
          duration: 0.32,
          attack: 0.015,
          decay: 0.06,
          sustainLevel: 0.4,
          release: 0.22,
          level: 0.09,
          reverbSend: 0.4,
          startAt: now + i * 0.04
        });
      });
    });
    vibrate(15);
  }

  // Triumphant final chord at end of session.
  gameEnd(): void {
    this.play("gameEnd", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Big major chord swelling in.
      const notes = [261.63, 329.63, 392.0, 523.25, 659.25];
      notes.forEach((freq, i) => {
        this.tone({
          freq,
          type: "triangle",
          duration: 1.2,
          attack: 0.05 + i * 0.015,
          decay: 0.2,
          sustainLevel: 0.5,
          release: 0.9,
          level: 0.13,
          reverbSend: 0.55,
          startAt: now + i * 0.03
        });
        this.tone({
          freq: freq * 2,
          type: "sine",
          duration: 1.0,
          attack: 0.06,
          decay: 0.15,
          sustainLevel: 0.35,
          release: 0.7,
          level: 0.045,
          reverbSend: 0.6,
          startAt: now + i * 0.03
        });
      });
      // Sparkle on top.
      this.noiseBurst({
        filterType: "bandpass",
        filterFreq: 4200,
        filterFreqEnd: 6800,
        filterQ: 1.8,
        duration: 0.4,
        level: 0.06,
        startAt: now + 0.15
      });
    });
    vibrate([20, 40, 30, 60]);
  }

  // Soft confirmation tap when picking a mode tab / era filter.
  tabSelect(): void {
    if (!this.canPlay("tab", 60)) return;
    this.play("tab", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.tone({
        freq: 880,
        type: "sine",
        duration: 0.1,
        attack: 0.005,
        decay: 0.02,
        sustainLevel: 0.0,
        release: 0.07,
        level: 0.08,
        reverbSend: 0.18,
        startAt: now
      });
    });
    vibrate(6);
  }

  // Slot-machine-style ascending tick during the score-counter animation.
  // `progress` (0–1) shapes the pitch so the climb sounds rising.
  scoreTick(progress: number): void {
    if (!this.canPlay("scoreTick", 60)) return;
    this.play("scoreTick", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Pitch climbs from 540 to 1320 as progress goes 0 → 1.
      const freq = 540 + Math.min(1, Math.max(0, progress)) * 780;
      this.tone({
        freq,
        type: "sine",
        duration: 0.07,
        attack: 0.002,
        decay: 0.018,
        sustainLevel: 0.0,
        release: 0.045,
        level: 0.06,
        startAt: now
      });
    });
  }

  // Discovery chime when the player opens a panorama hotspot.
  hotspot(): void {
    this.play("hotspot", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Two-note ascending bell — short and inquisitive.
      this.bell(880, 0.32, 0.13, now);
      this.bell(1318.51, 0.28, 0.1, now + 0.06);
      this.noiseBurst({
        filterType: "bandpass",
        filterFreq: 3600,
        filterFreqEnd: 5800,
        filterQ: 1.6,
        duration: 0.18,
        level: 0.04,
        startAt: now
      });
    });
    vibrate(8);
  }

  // Country / continent bonus chimes layered after a reveal stinger.
  bonus(type: "country" | "continent"): void {
    this.play("bonus", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      if (type === "country") {
        // Bigger reward — major triad arpeggio with bell timbre.
        [659.25, 783.99, 1046.5].forEach((freq, i) => {
          this.bell(freq, 0.42 - i * 0.04, 0.13, now + i * 0.06);
        });
      } else {
        // Continent: smaller — two bells.
        this.bell(523.25, 0.36, 0.11, now);
        this.bell(659.25, 0.32, 0.1, now + 0.06);
      }
    });
  }

  // Rising sweep that plays as the dashed polyline draws guess → answer.
  // `intensity` (0–1) modulates pitch and brightness — longer distance =
  // more dramatic sweep.
  distanceSweep(intensity = 0.5): void {
    const clamped = Math.max(0.1, Math.min(1, intensity));
    this.play("distanceSweep", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      const startFreq = 220;
      const endFreq = 220 + clamped * 880;
      const dur = 0.55 + clamped * 0.25;
      this.tone({
        freq: startFreq,
        freqEnd: endFreq,
        type: "sine",
        duration: dur,
        attack: 0.05,
        decay: 0.15,
        sustainLevel: 0.5,
        release: 0.3,
        level: 0.1,
        reverbSend: 0.5,
        startAt: now
      });
      this.noiseBurst({
        filterType: "bandpass",
        filterFreq: 800,
        filterFreqEnd: 1800 + clamped * 1800,
        filterQ: 1.2,
        duration: dur * 0.7,
        level: 0.04 + clamped * 0.04,
        startAt: now
      });
    });
  }

  // Low buzz for errors — softer than a punishing alarm but distinct.
  error(): void {
    this.play("error", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.tone({
        freq: 196,
        freqEnd: 147,
        type: "sawtooth",
        duration: 0.22,
        attack: 0.01,
        decay: 0.08,
        sustainLevel: 0.5,
        release: 0.12,
        level: 0.1,
        reverbSend: 0.15,
        startAt: now
      });
      this.tone({
        freq: 98,
        type: "sine",
        duration: 0.2,
        attack: 0.01,
        decay: 0.05,
        sustainLevel: 0.5,
        release: 0.1,
        level: 0.06,
        startAt: now
      });
    });
    vibrate([20, 60, 20]);
  }

  // Pin-reset / undo — soft descending click.
  pinReset(): void {
    this.play("pinReset", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.tone({
        freq: 880,
        freqEnd: 440,
        type: "sine",
        duration: 0.16,
        attack: 0.003,
        decay: 0.04,
        sustainLevel: 0.4,
        release: 0.1,
        level: 0.08,
        startAt: now
      });
    });
    vibrate(8);
  }

  // Onboarding step confirmation — a soft, encouraging click.
  onboardingStep(): void {
    this.play("onboardingStep", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.tone({
        freq: 587.33,
        type: "triangle",
        duration: 0.15,
        attack: 0.005,
        decay: 0.04,
        sustainLevel: 0.4,
        release: 0.09,
        level: 0.1,
        reverbSend: 0.3,
        startAt: now
      });
      this.tone({
        freq: 880,
        type: "sine",
        duration: 0.13,
        attack: 0.005,
        decay: 0.03,
        sustainLevel: 0.3,
        release: 0.08,
        level: 0.05,
        reverbSend: 0.35,
        startAt: now
      });
    });
  }

  // Streak milestone fanfare — distinct from per-round achievement.
  // Crescendo of bells with sparkle.
  streak(): void {
    this.play("streak", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      [440, 587.33, 659.25, 880, 1174.66].forEach((freq, i) => {
        this.bell(freq, 0.55 - i * 0.03, 0.14 + (i === 4 ? 0.03 : 0), now + i * 0.08);
      });
      this.noiseBurst({
        filterType: "bandpass",
        filterFreq: 4400,
        filterFreqEnd: 7200,
        filterQ: 2.0,
        duration: 0.5,
        level: 0.06,
        startAt: now + 0.12
      });
    });
    vibrate([15, 25, 15, 25, 30]);
  }

  // Year typed and confirmed (Enter / blur on year input). A two-note "lock"
  // — descending interval with a hard close, like a vault click.
  yearLock(): void {
    this.play("yearLock", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.tone({
        freq: 880,
        type: "triangle",
        duration: 0.12,
        attack: 0.003,
        decay: 0.025,
        sustainLevel: 0.5,
        release: 0.07,
        level: 0.12,
        reverbSend: 0.2,
        startAt: now
      });
      this.tone({
        freq: 587.33,
        type: "triangle",
        duration: 0.18,
        attack: 0.003,
        decay: 0.04,
        sustainLevel: 0.5,
        release: 0.12,
        level: 0.13,
        reverbSend: 0.25,
        startAt: now + 0.06
      });
      // Mechanical click underneath.
      this.noiseBurst({
        filterType: "highpass",
        filterFreq: 4200,
        filterQ: 0.8,
        duration: 0.04,
        level: 0.05,
        startAt: now + 0.06
      });
    });
    vibrate(12);
  }

  // Leaflet map zoom in/out — small pitched click.
  mapZoom(direction: "in" | "out" = "in"): void {
    if (!this.canPlay("mapZoom", 80)) return;
    this.play("mapZoom", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.tone({
        freq: direction === "in" ? 740 : 980,
        freqEnd: direction === "in" ? 980 : 740,
        type: "sine",
        duration: 0.1,
        attack: 0.003,
        decay: 0.025,
        sustainLevel: 0.4,
        release: 0.06,
        level: 0.06,
        startAt: now
      });
    });
  }

  // Whisper-soft preview when hovering a wager pill (desktop only — caller
  // checks pointer media query).
  wagerHover(wager: 1 | 2 | 3): void {
    if (!this.canPlay("wagerHover", 80)) return;
    this.play("wagerHover", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      const freq = wager === 1 ? 392 : wager === 2 ? 523.25 : 659.25;
      this.tone({
        freq,
        type: "sine",
        duration: 0.18,
        attack: 0.018,
        decay: 0.05,
        sustainLevel: 0.3,
        release: 0.1,
        level: 0.045,
        reverbSend: 0.25,
        startAt: now
      });
    });
  }

  // High-frequency sparkle synced to a confetti burst. Call repeatedly during
  // the burst window for a continuous shimmer.
  confettiSparkle(): void {
    if (!this.canPlay("confettiSparkle", 55)) return;
    this.play("confettiSparkle", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Random high-pitched ping in a celebratory range.
      const pitches = [1567.98, 1760, 1975.53, 2349.32, 2637.02];
      const freq = pitches[Math.floor(Math.random() * pitches.length)];
      this.tone({
        freq,
        type: "sine",
        duration: 0.13,
        attack: 0.003,
        decay: 0.03,
        sustainLevel: 0.0,
        release: 0.1,
        level: 0.05,
        reverbSend: 0.55,
        startAt: now
      });
    });
  }

  // Winding-spring sound when the comeback multiplier kicks in — a rising
  // tone with vibrato, like winding a clock back to life.
  comebackEngaged(): void {
    this.play("comeback", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Main rising tone with subtle vibrato (small pitch oscillation via
      // detune-modulated layer).
      this.tone({
        freq: 196,
        freqEnd: 587.33,
        type: "triangle",
        duration: 0.85,
        attack: 0.04,
        decay: 0.15,
        sustainLevel: 0.55,
        release: 0.4,
        level: 0.12,
        reverbSend: 0.5,
        startAt: now
      });
      this.tone({
        freq: 196,
        freqEnd: 587.33,
        type: "sine",
        duration: 0.85,
        attack: 0.05,
        decay: 0.18,
        sustainLevel: 0.4,
        release: 0.4,
        level: 0.07,
        detune: 18,
        reverbSend: 0.55,
        startAt: now
      });
      // Spring-rattle: stuttering high-pass noise pulses.
      for (let i = 0; i < 6; i++) {
        this.noiseBurst({
          filterType: "highpass",
          filterFreq: 2000 + i * 240,
          filterQ: 0.9,
          duration: 0.05,
          level: 0.025,
          startAt: now + 0.06 + i * 0.08
        });
      }
      // Lock-in chime at the top of the wind.
      this.bell(880, 0.4, 0.1, now + 0.7);
    });
    vibrate([12, 20, 12, 20, 25]);
  }

  // Soft thump when the end-of-game modal closes — subtle finality.
  modalThump(): void {
    this.play("modalThump", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      this.tone({
        freq: 196,
        freqEnd: 88,
        type: "sine",
        duration: 0.18,
        attack: 0.005,
        decay: 0.05,
        sustainLevel: 0.4,
        release: 0.12,
        level: 0.13,
        reverbSend: 0.2,
        startAt: now
      });
      this.noiseBurst({
        filterType: "lowpass",
        filterFreq: 600,
        filterQ: 0.8,
        duration: 0.08,
        level: 0.06,
        startAt: now
      });
    });
    vibrate(15);
  }

  // Sad-trombone "wah wah" for time-out / 0-point disasters. The classic
  // 3-staccato + 1-long-slide-down pattern, sawtooth body for that brassy
  // trombone bite, triangle layer underneath for warmth.
  wahWah(): void {
    this.play("wahWah", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      const notes = [
        { freq: 466.16, freqEnd: 440, duration: 0.3, start: 0.0 },     // Bb4 → A4
        { freq: 440, freqEnd: 415.3, duration: 0.3, start: 0.22 },     // A4 → Ab4
        { freq: 415.3, freqEnd: 392, duration: 0.36, start: 0.44 },    // Ab4 → G4
        { freq: 392, freqEnd: 261.63, duration: 0.85, start: 0.72 }    // G4 → C4 (long sad slide)
      ];
      notes.forEach((n) => {
        // Sawtooth body — that brassy "wah" bite.
        this.tone({
          freq: n.freq,
          freqEnd: n.freqEnd,
          type: "sawtooth",
          duration: n.duration,
          attack: 0.04,
          decay: 0.05,
          sustainLevel: 0.65,
          release: 0.18,
          level: 0.11,
          reverbSend: 0.3,
          startAt: now + n.start
        });
        // Triangle layer adds warmth so it's not just a buzzy saw.
        this.tone({
          freq: n.freq,
          freqEnd: n.freqEnd,
          type: "triangle",
          duration: n.duration,
          attack: 0.05,
          decay: 0.06,
          sustainLevel: 0.55,
          release: 0.16,
          level: 0.06,
          reverbSend: 0.4,
          startAt: now + n.start
        });
        // Sub-octave for body.
        this.tone({
          freq: n.freq / 2,
          freqEnd: n.freqEnd / 2,
          type: "sine",
          duration: n.duration,
          attack: 0.05,
          decay: 0.06,
          sustainLevel: 0.5,
          release: 0.15,
          level: 0.04,
          startAt: now + n.start
        });
      });
    });
    vibrate([30, 80, 30, 80, 60]);
  }

  // Reverby short "drop" sync'd to the orange answer pin appearing.
  answerPinPop(): void {
    this.play("answerPin", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      // Pitch sweeps down quickly — feels like dropping into place.
      this.tone({
        freq: 1320,
        freqEnd: 660,
        type: "sine",
        duration: 0.22,
        attack: 0.004,
        decay: 0.05,
        sustainLevel: 0.4,
        release: 0.16,
        level: 0.13,
        reverbSend: 0.6,
        startAt: now
      });
      // Bell partial for character.
      this.bell(660, 0.4, 0.08, now + 0.04);
    });
    vibrate(10);
  }

  // Zoom in/out: tiny pitch up or down.
  zoomTick(direction: "in" | "out" = "in"): void {
    if (!this.canPlay("zoom", 80)) return;
    this.play("zoom", () => {
      const ctx = this.ctx!;
      const now = ctx.currentTime;
      const start = direction === "in" ? 660 : 880;
      const end = direction === "in" ? 880 : 660;
      this.tone({
        freq: start,
        freqEnd: end,
        type: "sine",
        duration: 0.12,
        attack: 0.004,
        decay: 0.03,
        sustainLevel: 0.4,
        release: 0.07,
        level: 0.08,
        startAt: now
      });
    });
  }

  // Internals ----------------------------------------------------------------

  private effectiveGain(): number {
    if (this.muted) return 0;
    if (prefersReducedMotion()) return 0;
    return this.volume * 0.8;
  }

  private canPlay(name: SoundName, throttleMs: number): boolean {
    const now = performance.now();
    const last = this.lastPlayed.get(name) ?? 0;
    if (now - last < throttleMs) return false;
    this.lastPlayed.set(name, now);
    return true;
  }

  private play(name: SoundName, body: () => void): void {
    if (this.muted) return;
    if (prefersReducedMotion()) return;
    if (document.visibilityState !== "visible") return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      void ctx.resume();
    }
    void name;
    try {
      body();
    } catch {
      // Audio is non-critical — never let a synth error break gameplay.
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor: typeof AudioContext | undefined =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.effectiveGain();
    this.masterGain.connect(this.ctx.destination);
    this.setupReverb();
    return this.ctx;
  }

  private setupReverb(): void {
    const ctx = this.ctx!;
    // Simple short-tail reverb via feedback delay. Two short delays in series
    // give it a hall-y character without needing a convolution IR file.
    const inputDelay = ctx.createDelay(0.5);
    inputDelay.delayTime.value = 0.06;
    const tail = ctx.createGain();
    tail.gain.value = 0.42;
    const wet = ctx.createGain();
    wet.gain.value = 0.32;
    const damp = ctx.createBiquadFilter();
    damp.type = "lowpass";
    damp.frequency.value = 4200;
    inputDelay.connect(damp);
    damp.connect(tail);
    tail.connect(inputDelay);
    damp.connect(wet);
    wet.connect(this.masterGain!);
    this.reverbInput = inputDelay;
  }

  // Synth primitives ---------------------------------------------------------

  // Generic tone with optional ADSR + pitch sweep + reverb send.
  private tone(spec: ToneSpec): void {
    const ctx = this.ctx!;
    const now = spec.startAt ?? ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = spec.type ?? "sine";
    osc.frequency.setValueAtTime(spec.freq, now);
    if (spec.detune) osc.detune.setValueAtTime(spec.detune, now);
    if (spec.freqEnd !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(0.0001, spec.freqEnd), now + spec.duration);
    }

    const env = ctx.createGain();
    const peak = spec.level ?? 0.2;
    const attack = spec.attack ?? 0.005;
    const decay = spec.decay ?? 0.05;
    const sustain = (spec.sustainLevel ?? 0.5) * peak;
    const release = spec.release ?? 0.1;
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(peak, now + attack);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0001, sustain), now + attack + decay);
    env.gain.exponentialRampToValueAtTime(0.0001, now + spec.duration + release);

    osc.connect(env);
    if (spec.pan !== undefined && ctx.createStereoPanner) {
      const panner = ctx.createStereoPanner();
      panner.pan.value = clamp(-1, 1, spec.pan);
      env.connect(panner);
      panner.connect(this.masterGain!);
      if (spec.reverbSend && this.reverbInput) {
        const send = ctx.createGain();
        send.gain.value = spec.reverbSend;
        panner.connect(send);
        send.connect(this.reverbInput);
      }
    } else {
      env.connect(this.masterGain!);
      if (spec.reverbSend && this.reverbInput) {
        const send = ctx.createGain();
        send.gain.value = spec.reverbSend;
        env.connect(send);
        send.connect(this.reverbInput);
      }
    }

    osc.start(now);
    osc.stop(now + spec.duration + release + 0.05);
  }

  // Filtered noise burst — useful for whooshes and percussive transients.
  private noiseBurst(opts: {
    filterType?: BiquadFilterType;
    filterFreq: number;
    filterFreqEnd?: number;
    filterQ?: number;
    duration: number;
    level?: number;
    startAt?: number;
  }): void {
    const ctx = this.ctx!;
    const now = opts.startAt ?? ctx.currentTime;
    const bufferLen = Math.max(2, Math.floor(ctx.sampleRate * opts.duration));
    const buffer = ctx.createBuffer(1, bufferLen, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferLen; i++) {
      // Gentle decay envelope baked into the buffer.
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferLen);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = opts.filterType ?? "bandpass";
    filter.frequency.setValueAtTime(opts.filterFreq, now);
    if (opts.filterFreqEnd !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(opts.filterFreqEnd, now + opts.duration);
    }
    filter.Q.value = opts.filterQ ?? 0.8;

    const gain = ctx.createGain();
    const peak = opts.level ?? 0.15;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);

    src.connect(filter).connect(gain).connect(this.masterGain!);
    src.start(now);
    src.stop(now + opts.duration + 0.05);
  }

  // Bell-like additive synthesis: fundamental + inharmonic partials decaying
  // at different rates. Sounds far richer than a single sine.
  private bell(freq: number, duration: number, level: number, startAt?: number): void {
    const ctx = this.ctx!;
    const now = startAt ?? ctx.currentTime;
    const partials: Array<{ ratio: number; level: number; decay: number }> = [
      { ratio: 1, level: 1, decay: 1 },
      { ratio: 2.0, level: 0.45, decay: 0.85 },
      { ratio: 3.01, level: 0.25, decay: 0.65 },
      { ratio: 4.12, level: 0.16, decay: 0.5 },
      { ratio: 5.43, level: 0.1, decay: 0.4 }
    ];
    partials.forEach((p) => {
      this.tone({
        freq: freq * p.ratio,
        type: "sine",
        duration: duration * p.decay,
        attack: 0.002,
        decay: 0.04,
        sustainLevel: 0.4,
        release: duration * p.decay * 0.7,
        level: level * p.level,
        reverbSend: 0.45,
        startAt: now
      });
    });
  }

  // Major-chord fanfare for reveal stingers. Bigger, brighter for high tiers.
  private fanfareChord(freqs: number[], duration: number, brilliant: boolean): void {
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    freqs.forEach((freq, i) => {
      const start = now + i * 0.06;
      this.tone({
        freq,
        type: "triangle",
        duration,
        attack: 0.02,
        decay: 0.08,
        sustainLevel: 0.5,
        release: duration * 0.7,
        level: 0.16,
        reverbSend: 0.5,
        startAt: start
      });
      this.tone({
        freq: freq * 2,
        type: "sine",
        duration: duration * 0.85,
        attack: 0.025,
        decay: 0.07,
        sustainLevel: 0.4,
        release: duration * 0.55,
        level: 0.05,
        reverbSend: 0.55,
        startAt: start
      });
    });
    if (brilliant) {
      // Shimmer overlay for gold/perfect tiers.
      this.noiseBurst({
        filterType: "bandpass",
        filterFreq: 5200,
        filterFreqEnd: 8400,
        filterQ: 1.8,
        duration: duration * 0.7,
        level: 0.05,
        startAt: now
      });
    }
  }

  // Whoosh for modal open / close. Smoother than the old version: longer
  // envelope, more bandpass shaping, optional reverb tail.
  private swoosh(direction: "up" | "down"): void {
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    this.noiseBurst({
      filterType: "bandpass",
      filterFreq: direction === "up" ? 540 : 1800,
      filterFreqEnd: direction === "up" ? 2200 : 380,
      filterQ: 1.0,
      duration: 0.18,
      level: 0.13,
      startAt: now
    });
    // A soft tonal companion adds musicality.
    this.tone({
      freq: direction === "up" ? 392 : 523.25,
      freqEnd: direction === "up" ? 587.33 : 392,
      type: "sine",
      duration: 0.2,
      attack: 0.01,
      decay: 0.05,
      sustainLevel: 0.4,
      release: 0.12,
      level: 0.06,
      reverbSend: 0.4,
      startAt: now
    });
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function clamp(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(max, value));
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function vibrate(pattern: number | number[]): void {
  if (prefersReducedMotion()) return;
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // ignored
  }
}

export const feedback = new FeedbackService();
